import React, { useState, useEffect, useCallback } from "react";
import ReactPaginate from "react-paginate";
import ConsultaSelectionModal from "../ConsultaSelectionModal/ConsultaSelectionModal";
import AgendarConsultaModal from "../../Pages/AgendarConsultaModal";
import clinicaConfig from "../../clinicaConfig.js";
import { ReactComponent as Horaicon } from "../Assets/hora.svg";
import { ReactComponent as NoProfileIcon } from "../Assets/padrao.svg";
import './TodosMedicos.css';
import { FaStethoscope } from 'react-icons/fa';
import { ClipLoader } from "react-spinners";


const ITEMS_PER_PAGE = 12;

const TodosMedicos = () => {
  const [medicos, setMedicos] = useState([]);
  const [consultas, setConsultas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtroAtual, setFiltroAtual] = useState('Todos');
  const [medicosFiltrados, setMedicosFiltrados] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [currentItems, setCurrentItems] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [itemOffset, setItemOffset] = useState(0);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMedico, setSelectedMedico] = useState(null);
  const [consultasMedicoModal, setConsultasMedicoModal] = useState([]);

  const [isAgendarModalOpen, setIsAgendarModalOpen] = useState(false);
  const [consultaSelecionada, setConsultaSelecionada] = useState(null);

  const API_BASE_URL = clinicaConfig.REACT_APP_API_BASE_URL || 'http://localhost:4000';

  window.scrollTo({ top: 0, behavior: "smooth" });

  useEffect(() => {
    const fetchMedicosAndConsultas = async () => {
      try {
        setLoading(true);
        setError(null);

        const clientDomain = window.location.hostname;
        console.log('TodosMedicos - Domínio detectado:', clientDomain);

        const headers = {
          'X-Client-Domain': clientDomain,
          'Content-Type': 'application/json'
        };

        console.log('TodosMedicos - Fazendo requisição para:', `${API_BASE_URL}/public/medics`);
        const medicsResponse = await fetch(`${API_BASE_URL}/public/medics`, { headers });
        if (!medicsResponse.ok) {
          throw new Error(`Erro ao buscar médicos: ${medicsResponse.status} ${medicsResponse.statusText}`);
        }
        const medicsData = await medicsResponse.json();
        if (medicsData.success) {
          setMedicos(medicsData.medics);
          console.log('TodosMedicos - Médicos carregados:', medicsData.medics.length);
        } else {
          throw new Error(medicsData.message || 'Falha ao buscar médicos.');
        }

        console.log('TodosMedicos - Fazendo requisição para consultas disponiveis:', `${API_BASE_URL}/public/consultas/disponiveis`);
        const consultasResponse = await fetch(`${API_BASE_URL}/public/consultas/disponiveis`, {
          headers,
          cache: 'no-store'
        });
        if (!consultasResponse.ok) {
          throw new Error(`Erro ao buscar consultas disponiveis: ${consultasResponse.status} ${consultasResponse.statusText}`);
        }
        const consultasData = await consultasResponse.json();
        if (consultasData.success) {
          setConsultas(consultasData.consultas);
          console.log('TodosMedicos - Consultas disponiveis carregadas:', consultasData.consultas.length);
          console.log('TodosMedicos - Amostra de consultas disponíveis (até 10):', consultasData.consultas.slice(0, 10));
        } else {
          throw new Error(consultasData.message || 'Falha ao buscar consultas.');
        }

      } catch (err) {
        console.error("TodosMedicos - Erro ao carregar dados:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMedicosAndConsultas();
  }, [API_BASE_URL]);

  const obterDataAtual = () => {
    const hoje = new Date();
    return new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - 1);
  };

  const compararApenasData = (data1, data2) => {
    const d1 = new Date(data1);
    const d2 = new Date(data2);

    return d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate();
  };

  const getConsultasPorMedico = useCallback((medicoId) => {
    if (!consultas) return [];
    return consultas
      .filter(consulta => {
        const dataRaw = consulta.dataConsulta?.$date || consulta.dataConsulta;
        const dataConsulta = new Date(dataRaw);
        const dataAtual = obterDataAtual();

        return (
          String(consulta.medicId) === String(medicoId) &&
          consulta.status === 'disponivel' &&
          (dataConsulta >= dataAtual || compararApenasData(dataConsulta, dataAtual))
        );
      })
      .sort((a, b) => {
        const dateA = new Date(a.dataConsulta?.$date || a.dataConsulta);
        const dateB = new Date(b.dataConsulta?.$date || b.dataConsulta);
        return dateA - dateB;
      });
  }, [consultas]);

  const especialidades = React.useMemo(() => {
    const uniqueEspecialidades = [...new Set(medicos.map(medico => medico.especialidade))];
    return ['Todos', ...uniqueEspecialidades];
  }, [medicos]);

  useEffect(() => {
    const termo = searchTerm.toLowerCase();
    let filtrados = medicos.filter(medico => {
      const especialidadeMatch = filtroAtual === 'Todos' || medico.especialidade === filtroAtual;
      const nomeMatch = medico.nome.toLowerCase().includes(termo);
      const especialidadeTextoMatch = medico.especialidade.toLowerCase().includes(termo);
      return especialidadeMatch && (nomeMatch || especialidadeTextoMatch);
    });

    filtrados.sort((a, b) => {
      const consultasA = getConsultasPorMedico(a.id);
      const consultasB = getConsultasPorMedico(b.id);
      const proximaConsultaA = consultasA.length > 0 ? consultasA[0] : null;
      const proximaConsultaB = consultasB.length > 0 ? consultasB[0] : null;

      if (!proximaConsultaA && !proximaConsultaB) return 0;
      if (!proximaConsultaA) return 1;
      if (!proximaConsultaB) return -1;

      return new Date(proximaConsultaA.dataConsulta) - new Date(proximaConsultaB.dataConsulta);
    });

    setMedicosFiltrados(filtrados);
    setItemOffset(0);
  }, [filtroAtual, medicos, searchTerm, consultas, getConsultasPorMedico]);

  useEffect(() => {
    const endOffset = itemOffset + ITEMS_PER_PAGE;
    console.log(`Carregando itens de ${itemOffset} a ${endOffset}`);
    setCurrentItems(medicosFiltrados.slice(itemOffset, endOffset));
    setPageCount(Math.ceil(medicosFiltrados.length / ITEMS_PER_PAGE));
  }, [itemOffset, medicosFiltrados]);

  const handlePageClick = (event) => {
    const newOffset = (event.selected * ITEMS_PER_PAGE) % medicosFiltrados.length;
    setItemOffset(newOffset);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const formatarData = (dataInput) => {
    const dataRaw = dataInput?.$date || dataInput;
    const data = new Date(dataRaw);

    if (isNaN(data.getTime())) return "Data inválida";

    const dia = String(data.getUTCDate()).padStart(2, '0');
    const mes = String(data.getUTCMonth() + 1).padStart(2, '0');
    const ano = data.getUTCFullYear();
    return `${dia}/${mes}/${ano}`;
  };

 const getDateLabel = (isoDate) => {
    if (!isoDate) return 'Sem data';
    
    const d = new Date(isoDate + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    const dataFormatada = formatarData(isoDate);
    
    const sameDay = (x, y) => 
      x.getFullYear() === y.getFullYear() && 
      x.getMonth() === y.getMonth() && 
      x.getDate() === y.getDate();

    if (sameDay(d, today)) return `Hoje - ${dataFormatada}`;
    if (sameDay(d, tomorrow)) return `Amanhã - ${dataFormatada}`;
    return dataFormatada;
  };

  const handleConsultaClick = (medico, consulta) => {
    setSelectedMedico(medico);
    setConsultaSelecionada(consulta);
    setIsAgendarModalOpen(true);
  };

  const handleAgendarClick = (medico) => {
    const consultasDoMedico = getConsultasPorMedico(medico.id);
    console.log(`Consultas encontradas para ${medico.nome}:`, consultasDoMedico);

    if (consultasDoMedico.length > 1) {
      setSelectedMedico(medico);
      setConsultasMedicoModal(consultasDoMedico);
      setIsModalOpen(true);
    } else if (consultasDoMedico.length === 1) {
      const consultaUnica = consultasDoMedico[0];
      setSelectedMedico(medico);
      setConsultaSelecionada(consultaUnica);
      setIsAgendarModalOpen(true);
    } else {
      const mensagemGenerica = `Olá, gostaria de verificar a disponibilidade de agenda com ${medico.genero === "Masculino" ? "Dr." : "Dra."} ${medico.nome} (${medico.especialidade}). Há possibilidade de agendar uma consulta em breve?`;
      const whatsappUrl = `https://wa.me/${clinicaConfig.whatsappBot}?text=${encodeURIComponent(mensagemGenerica)}`;
      window.open(whatsappUrl, '_blank');
    }
  };

  const handleModalAgendar = (medicoSelecionado, consultaSelecionada) => {
    if (!consultaSelecionada || !consultaSelecionada.dataConsulta || !consultaSelecionada.horaConsulta) {
      console.error("Erro: Dados da consulta selecionada inválidos", consultaSelecionada);
      alert("Ocorreu um erro ao obter os detalhes da consulta. Tente novamente.");
      setIsModalOpen(false);
      return;
    }
    setSelectedMedico(medicoSelecionado);
    setConsultaSelecionada(consultaSelecionada);
    setIsModalOpen(false);
    setIsAgendarModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedMedico(null);
    setConsultasMedicoModal([]);
  };

  const handleCloseAgendarModal = () => {
    setIsAgendarModalOpen(false);
    setConsultaSelecionada(null);
  };

  if (loading) {
    return (
      <section className="tm-section tm-loading-section">
        <div className="tm-loading">
          <ClipLoader size={150} color="var(--primary)" />
          <p>Carregando médicos e consultas...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="tm-section">
        <h2 style={{ color: 'red' }}>Erro ao carregar dados: {error}</h2>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            marginTop: '10px'
          }}
        >
          Tentar Novamente
        </button>
      </section>
    );
  }

  return (
    <section className="tm-section">
      <div className="tm-header">
        <h1 className="tm-title">Nossos Especialistas</h1>
        <p className="tm-subtitle">Conheça todos os médicos disponíveis ({medicosFiltrados.length} encontrados)</p>
        <div className="list-consulta-header">
          <input
            type="text"
            placeholder="Buscar por nome ou especialidade"
            className="filtro-de-pesquisa"
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>
      </div>

      <div className="tm-filter-container">
        {especialidades.map((especialidade, index) => (
          <button
            key={index}
            className={`tm-filter-btn ${filtroAtual === especialidade ? 'tm-active' : ''}`}
            onClick={() => setFiltroAtual(especialidade)}
          >
            {especialidade}
          </button>
        ))}
      </div>


      <div className="tm-grid">
        {currentItems.length > 0 ? (
          currentItems.map(medico => {
            const consultasDoMedico = getConsultasPorMedico(medico.id);
            const consultasAgrupadas = consultasDoMedico.reduce((acc, consulta) => {
              const dataRaw = consulta.dataConsulta?.$date || consulta.dataConsulta;
              const dataKey = new Date(dataRaw).toISOString().split('T')[0]; 
              
              if (!acc[dataKey]) {
                acc[dataKey] = [];
              }
              acc[dataKey].push(consulta);
              return acc;
            }, {});
            
            const datasOrdenadas = Object.keys(consultasAgrupadas).sort();
            return (
              <div key={medico.id} className="tm-card">
                <div className="tm-card-header">
                  {medico.fotoUrl ? (
                    <img
                      src={medico.fotoUrl}
                      alt={`Foto de ${medico.nome}`}
                      className="tm-card-photo"
                      onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                    />
                  ) : (
                    <NoProfileIcon className="tm-card-photo" />
                  )}
                  <div className="tm-card-info">
                    <h3 className="tm-card-name">
                      {medico.genero === "Masculino" ? "Dr. " : "Dra. "}
                      {medico.nome}
                    </h3>
                    <p className="tm-card-crm">
                      {medico.especialidade === "Nutricionista" 
                        ? `CRN: ${medico.crm}` 
                        : medico.especialidade === "Biomédico" 
                        ? `CRBM: ${medico.crm}` 
                        : medico.especialidade === "Fisioterapeuta"
                        ? `Crefito: ${medico.crm}`
                        : (medico.especialidade === "Psicólogo" || medico.especialidade === "Psicóloga")
                        ? `CRP: ${medico.crm}`
                        : medico.especialidade === "Psiquiatria"
                        ? `RQE: ${medico.crm}`
                        : `CRM: ${medico.crm}`}
                    </p>
                  </div>
                </div>
                <div className="medic-card-content">
                  <div className="medic-card-specialty">
                    <FaStethoscope className="medic-card-specialty-icon" />
                    {medico.especialidade}
                  </div>
                </div>

                <div className="tm-consultas">
                  <h4 className="tm-consultas-titulo">Próximas consultas</h4>

                  {consultasDoMedico.length > 0 ? (
                    <div className="tm-consultas-lista">
                      
                      {datasOrdenadas.map((dataKey) => (
                        <div key={dataKey} className="tm-grupo-data">
                          
                          <div className="date-separator">
                            <div className="date-label">
                                {getDateLabel(dataKey)}
                            </div>
                            <div className="date-count">
                                {consultasAgrupadas[dataKey].length} {consultasAgrupadas[dataKey].length === 1 ? 'Horário' : 'Horários'}
                            </div>
                          </div>

                          {consultasAgrupadas[dataKey].map((consulta) => (
                            <div 
                              key={consulta._id || consulta.id} 
                              className="tm-consulta-item"
                              onClick={() => handleConsultaClick(medico, consulta)}
                            >
                              
                              <div className="tm-consulta-hora">
                                <Horaicon className="tm-icon" />
                                <span>{consulta.horaConsulta}</span>
                              </div>

                              <div className="tm-consulta-valor">
                                R${consulta.valorConsulta},00
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}

                    </div>
                  ) : (
                    <div className="tm-sem-consultas">
                      Este profissional ainda não possui consultas agendadas para os próximos dias.
                    </div>
                  )}
                </div>

                {consultasDoMedico.length > 0 ? (
                  <button
                    onClick={() => handleAgendarClick(medico)}
                    className="tm-agendar-btn"
                  >
                    <i className="fab fa-whatsapp"></i>
                    <span>Agendar Consulta</span>
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => handleAgendarClick(medico)}
                      className="tm-agendar-btn"
                    >
                      <i className="fab fa-whatsapp"></i>
                      <span>Entrar em contato para verificar disponibilidade</span>
                    </button>
                  </>
                )}
              </div>
            );
          })
        ) : (
          <div className="tm-sem-resultados">
            Nenhum médico encontrado.
          </div>
        )}
      </div>

      {pageCount > 1 && (
        <ReactPaginate
          breakLabel="..."
          nextLabel="Próximo >"
          onPageChange={handlePageClick}
          pageRangeDisplayed={3}
          marginPagesDisplayed={1}
          pageCount={pageCount}
          previousLabel="< Anterior"
          containerClassName="tm-pagination"
          activeClassName="tm-active"
          pageClassName="tm-page-item"
          pageLinkClassName="tm-page-link"
          previousClassName="tm-page-item"
          previousLinkClassName="tm-page-link"
          nextClassName="tm-page-item"
          nextLinkClassName="tm-page-link"
          breakClassName="tm-page-item"
          breakLinkClassName="tm-page-link"
          renderOnZeroPageCount={null}
        />
      )}

      <ConsultaSelectionModal
        isOpen={isModalOpen}
        onRequestClose={handleCloseModal}
        medico={selectedMedico}
        consultas={consultasMedicoModal}
        onAgendar={handleModalAgendar}
      />

      <AgendarConsultaModal
        isOpen={isAgendarModalOpen}
        onClose={handleCloseAgendarModal}
        consulta={consultaSelecionada}
        medico={selectedMedico}
      />
    </section>
  );
};


export default TodosMedicos;
